import { usePlacesWidget } from "react-google-autocomplete";
import {
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  Typography,
  Box,
  Theme,
  SxProps,
} from "@mui/material";
import { Copy } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import "./AddressAutocomplete.css";

export type Location = {
  latitude: number;
  longitude: number;
  address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  formattedAddress: string;
  name?: string;
};

const getAddressComponent = (
  components: { long_name: string; short_name: string; types: string[] }[],
  type: string,
  shortName = false,
) => {
  const component = components.find((c) => c.types.includes(type));
  if (!component) return "";
  return shortName ? component.short_name : component.long_name;
};

type Props = {
  label: string;
  error?: string;
  name?: string;
  autoComplete?: string;
  initialValue?: string;
  disabled?: boolean;
  onBlur?: () => void;
  setValue: (location: Location) => void;
  handleCopy?: boolean;
  restrictToAddresses?: boolean;
  inputSx?: SxProps<Theme>;
};

export default ({
  label,
  error,
  name,
  initialValue,
  disabled,
  onBlur,
  setValue,
  handleCopy,
  restrictToAddresses = true, // Default to restricting to addresses
  autoComplete,
  inputSx,
}: Props) => {
  const [copied, setCopied] = useState(false);
  const hasSelectedFromAutocompleteRef = useRef(false);

  const locationFromComponents = (
    components: google.maps.GeocoderAddressComponent[],
    latLng: google.maps.LatLng,
    formattedAddress: string,
    name?: string,
  ): Location => {
    const streetNumber = getAddressComponent(components, "street_number");
    const route = getAddressComponent(components, "route");
    const address =
      streetNumber && route ? `${streetNumber} ${route}` : formattedAddress;
    return {
      latitude: latLng.lat(),
      longitude: latLng.lng(),
      address,
      unit: getAddressComponent(components, "subpremise"),
      city:
        getAddressComponent(components, "locality") ||
        getAddressComponent(components, "sublocality"),
      state: getAddressComponent(components, "administrative_area_level_1"),
      zip: getAddressComponent(components, "postal_code"),
      country: getAddressComponent(components, "country", true),
      formattedAddress,
      name,
    };
  };

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    if (
      !place.geometry ||
      !place.address_components ||
      !place.formatted_address ||
      !place.geometry.location
    )
      return;

    let displayValue = place.formatted_address;
    if (
      place.name &&
      !place.formatted_address
        .toLowerCase()
        .startsWith(place.name.toLowerCase())
    ) {
      displayValue = `${place.name} — ${place.formatted_address}`;
    }
    if (materialRef.current) {
      materialRef.current.value = displayValue;
    }

    hasSelectedFromAutocompleteRef.current = true;
    setValue(
      locationFromComponents(
        place.address_components,
        place.geometry.location,
        place.formatted_address,
        place.name,
      ),
    );
  };
  const { ref: materialRef } = usePlacesWidget<HTMLInputElement>({
    onPlaceSelected: (place) => {
      handlePlaceSelected(place);
    },
    language: "en",
    inputAutocompleteValue: "street-address",
    options: {
      types: restrictToAddresses ? ["address"] : [], // Restrict to addresses or allow any type
      componentRestrictions: { country: ["us", "ca"] },
      fields: ["address_components", "formatted_address", "geometry", "name"],
    },
  });

  useEffect(() => {
    const input = materialRef.current;
    if (!input) return;
    // Native listener fires for iOS autofill even when React's synthetic onChange doesn't,
    // preventing a stale hasSelectedFromAutocompleteRef from skipping the geocode fallback.
    const resetFlag = () => {
      hasSelectedFromAutocompleteRef.current = false;
    };
    input.addEventListener("input", resetFlag);
    return () => input.removeEventListener("input", resetFlag);
  }, []);

  const handleBlur = async () => {
    if (!materialRef.current?.value) {
      setValue({
        latitude: 0,
        longitude: 0,
        address: "",
        unit: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        formattedAddress: "",
      });
      onBlur?.();
      return;
    }

    if (hasSelectedFromAutocompleteRef.current) {
      hasSelectedFromAutocompleteRef.current = false;
      onBlur?.();
      return;
    }

    if (!restrictToAddresses) {
      setValue({
        latitude: 0,
        longitude: 0,
        address: "",
        unit: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        formattedAddress: "",
        name: materialRef.current.value,
      });
      onBlur?.();
      return;
    }

    // iOS autofill (and manual entry) fills the input without going through Places.
    // Geocode the text to get structured location data.
    const rawText = materialRef.current.value;
    try {
      const geocoder = new google.maps.Geocoder();
      const { results } = await geocoder.geocode({ address: rawText });
      if (results.length > 0) {
        const r = results[0];
        setValue(
          locationFromComponents(
            r.address_components,
            r.geometry.location,
            r.formatted_address,
          ),
        );
        if (materialRef.current)
          materialRef.current.value = r.formatted_address;
      } else {
        setValue({
          latitude: 0,
          longitude: 0,
          address: rawText,
          unit: "",
          city: "",
          state: "",
          zip: "",
          country: "",
          formattedAddress: rawText,
        });
      }
    } catch {
      setValue({
        latitude: 0,
        longitude: 0,
        address: rawText,
        unit: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        formattedAddress: rawText,
      });
    }

    onBlur?.();
  };

  const handleCopyClick = async () => {
    if (materialRef.current) {
      const textToCopy = materialRef.current.value;
      if (textToCopy) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    }
  };

  return (
    <TextField
      error={!!error}
      helperText={error}
      label={label}
      name={name}
      autoComplete={autoComplete}
      fullWidth
      color="secondary"
      variant="outlined"
      inputRef={materialRef}
      margin="normal"
      size="small"
      defaultValue={initialValue}
      disabled={disabled}
      inputProps={{
        autoComplete: !autoComplete ? "street-address" : autoComplete,
      }}
      onChange={() => {
        // User is manually typing, reset autocomplete flag
        hasSelectedFromAutocompleteRef.current = false;
      }}
      onBlur={handleBlur}
      sx={{ marginTop: 0, marginBottom: 0, ...inputSx }}
      InputProps={{
        endAdornment: handleCopy &&
          (materialRef?.current?.value || initialValue) && (
            <InputAdornment
              position="end"
              sx={{
                paddingRight: "12px !important",
                paddingTop: "0 !important",
                paddingLeft: "15px !important",
              }}
            >
              <Box position="relative" display="flex" alignItems="center">
                <Fade in={!copied} timeout={300}>
                  <IconButton
                    onClick={handleCopyClick}
                    edge="end"
                    disabled={!(materialRef?.current?.value || initialValue)}
                  >
                    <Copy width="20" />
                  </IconButton>
                </Fade>
                <Fade in={copied} timeout={300}>
                  <Typography
                    variant="body"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      paddingRight: "10px",
                    }}
                  >
                    Copied
                  </Typography>
                </Fade>
              </Box>
            </InputAdornment>
          ),
      }}
    />
  );
};
